import * as React from "react";
import { useTranslations } from "next-intl";
import { ConfirmDialog, useToast } from "@chotca/ui";
import { getSdk } from "@chotca/api-client";

type Props = {
  open: boolean;
  onClose: () => void;
  email: string | null | undefined;
};

/**
 * Confirms before triggering a password reset. This is a reset-via-email flow
 * (sends a link through requestPasswordReset), not an in-app password change,
 * so the dialog spells out that an email will be sent before doing it.
 */
export function ChangePasswordDialog({ open, onClose, email }: Props) {
  const t = useTranslations("avatarMenu");
  const tCommon = useTranslations("common");
  const { toast } = useToast();
  const [sending, setSending] = React.useState(false);

  const handleConfirm = async () => {
    if (!email) {
      toast({ tone: "error", title: t("passwordResetNoEmail") });
      onClose();
      return;
    }
    if (sending) return;
    setSending(true);
    try {
      await getSdk().auth.requestPasswordReset({ email });
      toast({ tone: "success", title: t("passwordResetSent") });
      onClose();
    } catch {
      toast({ tone: "error", title: t("passwordResetError") });
    } finally {
      setSending(false);
    }
  };

  return (
    <ConfirmDialog
      open={open}
      onClose={() => {
        if (!sending) onClose();
      }}
      onConfirm={handleConfirm}
      loading={sending}
      title={t("passwordResetConfirmTitle")}
      confirmLabel={t("passwordResetConfirmCta")}
      cancelLabel={tCommon("cancel")}
      description={
        <span className="block space-y-2">
          <span className="block">{t("passwordResetConfirmBody")}</span>
          {email ? (
            <span className="block truncate rounded-lg bg-mist px-3 py-2 font-semibold text-ink">
              {email}
            </span>
          ) : null}
          <span className="block text-xs text-ash">
            {t("passwordResetConfirmHint")}
          </span>
        </span>
      }
    />
  );
}
